import { Map2D } from 'common/Prose/utils/table/Map2D'
import { TableMap } from 'common/Prose/utils/table/TableMap'
import { ResolvedPos } from 'prosemirror-model'
import { type EditorView } from 'prosemirror-view'
import * as React from 'react'
import styles from './CellBorder.module.scss'
import cx from 'classnames'
import { isTableCell } from 'common/Prose/utils/table'
import { Node as ProseNode } from 'prosemirror-model'

export type borderAttrs = ''

type cellWithCoords = {
  cell: ResolvedPos
  coords: [number, number][]
}

export type CellBorderProps = {
  view: EditorView
  node: ProseNode
  getPos: () => number
}

export const CellBorder: React.FC<React.PropsWithChildren<CellBorderProps>> = (props) => {
  const { view, getPos, node } = props
  if (isNaN(getPos())) return null
  const tableMap = new TableMap({ posBeforeTable: getPos(), node })
  const rowFragments = tableMap.getRowFragments()
  const columnFragments = tableMap.getColFragments()

  const horizontalMap = new Map2D<borderAttrs>()
  const verticalMap = new Map2D<borderAttrs>()
  if (view && tableMap) {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const cells: cellWithCoords[] = tableMap!
      .getPosBeforeCellList() // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      .map((pos) => ({ cell: view.state.doc.resolve(pos), coords: tableMap!.getCoordListOfPos(pos) }))

    cells.forEach((cell) => {
      cell.coords.forEach(([rowIndex, colIndex]) => {
        if (cell.cell.nodeAfter && isTableCell(cell.cell.nodeAfter)) {
          horizontalMap.set(rowIndex, colIndex, cell.cell.nodeAfter.attrs.borderTop)
          horizontalMap.set(rowIndex + 1, colIndex, cell.cell.nodeAfter.attrs.borderBottom)
          verticalMap.set(rowIndex, colIndex, cell.cell.nodeAfter.attrs.borderLeft)
          verticalMap.set(rowIndex, colIndex + 1, cell.cell.nodeAfter.attrs.borderRight)
        }
      })
    })
  }

  return (
    <>
      {Array.from(horizontalMap.entries()).map(([[rowIdx, colIdx], borderTop]) => {
        const borderThickness = parseFloat(borderTop.split(' ')[0])
        if (borderThickness === 0) return null
        const halfBorderThickness = Math.floor(borderThickness / 2)

        const top = rowIdx === 0
        const bottom = rowIdx === tableMap.numRows
        const left = colIdx === 0
        const right = colIdx === tableMap.numCols - 1
        let transformOffsets: [number, number] = [-halfBorderThickness, 0]

        if (top) transformOffsets[1] = halfBorderThickness
        if (bottom) transformOffsets[1] = -halfBorderThickness
        if (left) transformOffsets[0] = 0

        return rowFragments[rowIdx].some(([from, to]) => from <= colIdx && colIdx <= to) ? (
          <div
            key={`RowFragment:${rowIdx}-${colIdx}`}
            className={cx(styles.border, styles.horizontal, { [styles.evenThickness]: borderThickness % 2 === 0 })}
            style={{
              height: borderTop.split(' ')[0],
              gridColumn: `${(colIdx + 1) * 2 + 1}/${(colIdx + 1) * 2 + 2}`,
              gridRow: `${(rowIdx + 1) * 2} / span 1`,
              borderTop: borderTop,
              transform: `translate(${transformOffsets[0]}px, ${transformOffsets[1]}px)`,
              width:
                left || right ? `calc(100% + ${halfBorderThickness}px)` : `calc(100% + ${halfBorderThickness * 2}px)`,
              zIndex: borderThickness,
            }}
          />
        ) : null
      })}

      {Array.from(verticalMap.entries()).map(([[rowIdx, colIdx], borderLeft]) => {
        const borderThickness = parseFloat(borderLeft.split(' ')[0])
        if (borderThickness === 0) return null
        const halfBorderThickness = Math.floor(borderThickness / 2)

        const top = rowIdx === 0
        const bottom = rowIdx === tableMap.numRows - 1
        const left = colIdx === 0
        const right = colIdx === tableMap.numCols

        let transformOffsets: [number, number] = [0, -halfBorderThickness]

        if (left) transformOffsets[0] = halfBorderThickness
        if (right) transformOffsets[0] = -halfBorderThickness
        if (top) transformOffsets[1] = 0

        return columnFragments[colIdx].some(([from, to]) => from <= rowIdx && rowIdx <= to) ? (
          <div
            key={`ColumnFragment:${rowIdx}-${colIdx}`}
            className={cx(styles.border, styles.vertical, { [styles.evenThickness]: borderThickness % 2 === 0 })}
            style={{
              width: borderLeft.split(' ')[0],
              gridRow: `${(rowIdx + 1) * 2 + 1}/${(rowIdx + 1) * 2 + 2}`,
              gridColumn: `${(colIdx + 1) * 2} / span 1`,
              borderLeft: borderLeft,
              transform: `translate(${transformOffsets[0]}px, ${transformOffsets[1]}px)`,
              height:
                top || bottom ? `calc(100% + ${halfBorderThickness}px)` : `calc(100% + ${halfBorderThickness * 2}px)`,
              zIndex: borderThickness,
            }}
          />
        ) : null
      })}
    </>
  )
}
